
import { useEffect, useState } from "react";
import { API_BASE } from "../config/appConfig";
import TaskCard from "./TaskCard";
import { STATUS } from "../config/taskData";
import { formatDate, getDeadlineCountdown, getDeadlineParts } from "../utils/tasks";
import { InlineIcon, UiIcon } from "./Icon";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock3,
  MapPin,
  RefreshCw,
  ArrowLeft,
} from "lucide-react";

const BACKEND_TO_UI_STATUS = {
  new: "new",
  accepted: "in_progress",
  completed: "done",
  issue: "problem",
};

const UI_TO_BACKEND_STATUS = {
  in_progress: "accepted",
  done: "completed",
  problem: "issue",
};

function normalizeTask(task) {
  const raw = task.deadline ? task.deadline : null;
  const { date, time } = getDeadlineParts(raw);
  const contact = task.contractor_details
    ? {
        name: task.contractor_details.name,
        phone: task.contractor_details.phone || "",
        position: task.contractor_details.position || "",
        company: task.contractor_details.company || "",
      }
    : null;

  return {
    id: task.id,
    access_token: task.access_token,
    name: task.name || task.description || "Bez nazwy",
    desc: task.description || task.name,
    location: task.location || "",
    contact,
    status: BACKEND_TO_UI_STATUS[task.status] || "new",
    date,
    time,
    deadlineAt: raw,
    event_name: task.event_name || "",
  };
}

export default function ContractorTaskListScreen({ contractorUuid }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [doneExpanded, setDoneExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      setDoneExpanded(false);
      try {
        const res = await fetch(`${API_BASE}/api/tasks/contractor/${contractorUuid}/`);
        if (!res.ok) {
          setError("Nie znaleziono twoich zadań.");
          setTasks([]);
          return;
        }
        const data = await res.json();
        setTasks(data.map(normalizeTask));
      } catch {
        setError("Błąd połączenia.");
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [contractorUuid]);

  const updateStatus = async (task, nextUiStatus) => {
    const backendStatus = UI_TO_BACKEND_STATUS[nextUiStatus];
    if (!backendStatus || updating) return;
    setUpdating(true);
    try {
      const res = await fetch(`${API_BASE}/api/tasks/token/${task.access_token}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: backendStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTask(normalizeTask(data));
        // Also update in list
        setTasks((prev) => prev.map((t) => (t.id === task.id ? normalizeTask(data) : t)));
      }
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 32 }}>Ładowanie zadań...</div>;
  }
  if (error) {
    return <div style={{ color: "var(--color-danger)", textAlign: "center", padding: 32 }}>{error}</div>;
  }
  if (selectedTask) {
    const task = selectedTask;
    const st = STATUS[task.status];
    const isDone = task.status === "done";
    const deadlineCountdown = task.deadlineAt ? getDeadlineCountdown(task.deadlineAt) : null;
    const displayDeadlineCountdown = deadlineCountdown?.replace(/^Za\s+/, "");
    const isDeadlineOverdue = deadlineCountdown?.startsWith("Po terminie");
    const actions = {
      new: [
        { label: "Przyjmuję zadanie", icon: Check, next: "in_progress", color: "var(--color-primary)", bg: "var(--color-primary-light)", border: "var(--color-primary-border)" },
      ],
      in_progress: [
        { label: "Zadanie wykonane", icon: Check, next: "done", color: "var(--badge-done-text)", bg: "var(--badge-done-bg)", border: "var(--badge-done-border)" },
        { label: "Zgłoś problem", icon: AlertTriangle, next: "problem", color: "var(--badge-problem-text)", bg: "var(--badge-problem-bg)", border: "var(--badge-problem-border)" },
      ],
      done: [],
      problem: [
        { label: "Wznawiam realizację", icon: RefreshCw, next: "in_progress", color: "var(--badge-progress-text)", bg: "var(--badge-progress-bg)", border: "var(--badge-progress-border)" },
      ],
    };
    return (
      <div style={{ fontFamily: "var(--font-body)", background: "var(--color-bg-app)", minHeight: "100dvh", maxWidth: 480, width: "calc(100% - 16px)", margin: "0 auto" }}>
        <div style={{ background: "var(--color-bg-card)", borderBottom: "1px solid var(--color-border)", padding: "20px 20px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => setSelectedTask(null)} style={{ background: "none", border: "none", color: "var(--color-primary)", cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", gap: 4 }}>
            <InlineIcon Icon={ArrowLeft} /> Lista zadań
          </button>
          <div style={{ flex: 1 }} />
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: st.bg, borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, border: `1px solid ${st.border}` }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: st.dot, display: "inline-block", flexShrink: 0 }} />
            <span style={{ color: st.color, fontWeight: "var(--font-semibold)", fontSize: "var(--text-xs)" }}>{st.label}</span>
          </div>
          <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: 20 }}>
            <p style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h3)", color: "var(--color-text-primary)", fontWeight: "var(--font-semibold)", lineHeight: "var(--leading-text)", margin: "0 0 12px" }}>
              {task.desc || "Brak opisu"}
            </p>
            {(task.date || task.time) && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "flex-start",
                    gap: 8,
                    background: "var(--color-bg-subtle)",
                    color: !isDone && isDeadlineOverdue ? "var(--color-warning)" : "var(--color-text-muted)",
                    padding: "8px 10px",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-medium)",
                    border: "1px solid var(--color-border)",
                    lineHeight: 1.4,
                  }}
                >
                  <InlineIcon Icon={Clock3} style={{ marginTop: 1 }} />
                  <div>
                    <div>
                      Do: {task.date && formatDate(task.deadlineAt || task.date)}
                      {task.time ? ` ${task.time}` : ""}
                    </div>
                    {!isDone && task.date && deadlineCountdown && (
                      <div>
                        Pozostało: {isDeadlineOverdue ? "Po terminie" : displayDeadlineCountdown}
                      </div>
                    )}
                  </div>
                </div>
                {task.location && (
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "var(--color-bg-subtle)",
                      color: "var(--color-text-muted)",
                      padding: "4px 10px",
                      borderRadius: "var(--radius-md)",
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-medium)",
                      border: "1px solid var(--color-border)",
                    }}
                  >
                    <InlineIcon Icon={MapPin} style={{ marginRight: 2 }} />
                    {task.location}
                  </div>
                )}
              </div>
            )}
          </div>
          {isDone ? (
            <div style={{ background: "var(--badge-done-bg)", border: "1px solid var(--badge-done-border)", borderRadius: "var(--radius-lg)", padding: 24, textAlign: "center" }}>
              <div style={{ marginBottom: 8, display: "inline-flex" }}><UiIcon Icon={CheckCircle2} /></div>
              <div style={{ color: "var(--color-success)", fontWeight: "var(--font-semibold)", fontSize: "var(--text-body)" }}>Zadanie zakończone</div>
              <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-sm)", marginTop: 4 }}>Świetna robota!</div>
            </div>
          ) : (
            actions[task.status]?.map((a) => (
              <button
                key={a.next}
                onClick={() => updateStatus(task, a.next)}
                disabled={updating}
                style={{ background: a.bg, color: a.color, border: `1.5px solid ${a.border}`, borderRadius: "var(--radius-lg)", padding: 18, fontWeight: "var(--font-semibold)", fontSize: "var(--text-body)", cursor: updating ? "default" : "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, opacity: updating ? 0.7 : 1 }}
              >
                <span style={{ display: "inline-flex" }}><UiIcon Icon={a.icon} /></span>
                {updating ? "Zapisywanie..." : a.label}
              </button>
            ))
          )}
        </div>
      </div>
    );
  }

  if (!tasks.length) {
    return <div style={{ textAlign: "center", padding: 32 }}>Brak twoich zadań.</div>;
  }

  const activeTasks = tasks.filter((task) => task.status !== "done");
  const doneTasks = tasks.filter((task) => task.status === "done");

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: 16 }}>
      <h2 style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-h2)", marginBottom: 16 }}>Zadania do wykonania</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {activeTasks.map((task) => (
          <div
            key={task.id}
            style={{ border: "1px solid var(--color-border)", borderRadius: 12, background: "var(--color-bg-card)", cursor: "pointer" }}
            onClick={() => setSelectedTask(task)}
          >
            <TaskCard task={task} hideContractor />
          </div>
        ))}

        {doneTasks.length > 0 && (
          <div style={{ border: "1px solid var(--color-border)", borderRadius: 12, overflow: "hidden", background: "var(--color-bg-card)" }}>
            <button
              onClick={() => setDoneExpanded((prev) => !prev)}
              style={{
                width: "100%",
                border: "none",
                borderBottom: doneExpanded ? "1px solid var(--color-border)" : "none",
                background: "var(--color-bg-subtle)",
                color: "var(--color-text-primary)",
                padding: "12px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: "var(--font-semibold)", fontSize: "var(--text-sm)" }}>Done</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 22,
                    height: 20,
                    borderRadius: 999,
                    padding: "0 7px",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--color-success)",
                    background: "var(--badge-done-bg)",
                    border: "1px solid var(--badge-done-border)",
                  }}
                >
                  {doneTasks.length}
                </span>
              </span>
              <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>{doneExpanded ? "Ukryj" : "Pokaż"}</span>
            </button>

            {doneExpanded && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 12 }}>
                {doneTasks.map((task) => (
                  <div
                    key={task.id}
                    style={{ border: "1px solid var(--color-border)", borderRadius: 12, background: "var(--color-bg-card)", cursor: "pointer" }}
                    onClick={() => setSelectedTask(task)}
                  >
                    <TaskCard task={task} hideContractor />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
